import { useSyncExternalStore } from 'react'

/**
 * Sensibilidade da câmera: o quanto girar, aproximar e arrastar o quadro
 * respondem ao dedo ou ao mouse. No celular a órbita padrão do Three.js é
 * rápida demais para ver um mosquetão de perto; a pessoa escolhe o nível no
 * menu da câmera e a escolha fica guardada neste navegador.
 */

export type NivelSensibilidade = 'suave' | 'media' | 'rapida'

export interface AjusteCamera {
  /** Velocidade da órbita (girar em volta do alvo). */
  readonly girar: number
  /** Velocidade do zoom (pinça, roda do mouse). */
  readonly aproximar: number
  /** Velocidade de arrastar o quadro (dois dedos, botão direito). */
  readonly arrastar: number
  /**
   * Amortecimento da órbita: quanto maior, mais cedo a câmera para depois de
   * soltar o dedo (menos "deslize").
   */
  readonly amortecimento: number
  /** Velocidade de andar com as setas do teclado, em m/s. */
  readonly andarMPorS: number
}

export const AJUSTES_CAMERA: Readonly<Record<NivelSensibilidade, AjusteCamera>> = {
  suave: { girar: 0.35, aproximar: 0.45, arrastar: 0.6, amortecimento: 0.2, andarMPorS: 1.2 },
  media: { girar: 0.6, aproximar: 0.7, arrastar: 0.9, amortecimento: 0.14, andarMPorS: 2.5 },
  rapida: { girar: 1, aproximar: 0.9, arrastar: 1.2, amortecimento: 0.08, andarMPorS: 5 },
}

export const NIVEIS_SENSIBILIDADE: ReadonlyArray<{ nivel: NivelSensibilidade; rotulo: string }> = [
  { nivel: 'suave', rotulo: 'Suave' },
  { nivel: 'media', rotulo: 'Média' },
  { nivel: 'rapida', rotulo: 'Rápida' },
]

/** A câmera nasce mais calma que a órbita padrão: a rápida é a de antes. */
export const NIVEL_PADRAO: NivelSensibilidade = 'media'

// a chave guarda o nome antigo do projeto (BIZU-LAB), como as demais deste navegador
const CHAVE = 'bizu-lab-sensibilidade-camera'

const ehNivel = (valor: unknown): valor is NivelSensibilidade => valor === 'suave' || valor === 'media' || valor === 'rapida'

function ler(): NivelSensibilidade {
  try {
    const salvo = typeof window !== 'undefined' ? window.localStorage.getItem(CHAVE) : null
    return ehNivel(salvo) ? salvo : NIVEL_PADRAO
  } catch {
    return NIVEL_PADRAO
  }
}

let atual: NivelSensibilidade | null = null
const ouvintes = new Set<() => void>()

export function nivelDaCamera(): NivelSensibilidade {
  atual ??= ler()
  return atual
}

export function definirNivelDaCamera(nivel: NivelSensibilidade): void {
  atual = nivel
  try {
    window.localStorage.setItem(CHAVE, nivel)
  } catch {
    // sem armazenamento (janela privada): vale só nesta visita
  }
  for (const ouvinte of ouvintes) ouvinte()
}

function assinar(ouvinte: () => void): () => void {
  ouvintes.add(ouvinte)
  return () => ouvintes.delete(ouvinte)
}

/** O nível escolhido; quem o lê é redesenhado quando ele muda. */
export const useNivelDaCamera = (): NivelSensibilidade => useSyncExternalStore(assinar, nivelDaCamera, () => NIVEL_PADRAO)
